/**
 * /try Share Navigation (Same-Tab Bootstrap Wait + New-Tab Direct URL).
 *
 * WHAT THIS FILE DOES:
 * - Same-tab mode: waits for StoryStream bootstrap before navigation.
 * - New-tab mode: resolves and returns share publicUrl immediately.
 * - Stores WS credentials in sessionStorage for same-tab share-shell fast-path.
 *
 * WHY THIS EXISTS:
 * - Keeps try-ui.ts focused on DOM orchestration (no god module).
 * - Makes the “navigate on first token” contract explicit and testable.
 * - Centralizes sessionStorage handoff rules for same-origin shares.
 *
 * CALLED BY:
 * - src/client/try-ui.ts during submit flow.
 *
 * THIS FILE CALLS:
 * - connectTryWebSocket() to await story_bootstrap.
 * - shouldNavigateOnBootstrap() for bootstrap detection.
 *
 * @file src/client/try-share-navigation.ts
 */

import type { IngestResponse, ShareResponse } from './try-shared.js';
import { WebTryError } from './try-shared.js';
import { connectTryWebSocket, type TryWebSocketOptions } from './try-ws.js';
import { shouldNavigateOnBootstrap } from './try-stream-detect.js';

const StreamingTimeouts = Object.freeze({
  /**
   * Max time to wait for the bootstrap frame before falling back.
   */
  BOOTSTRAP_TIMEOUT_MS: 8_000
});

const ShareStreamingHandoff = Object.freeze({
  /**
   * SessionStorage key for /try → share WS credential handoff.
   */
  STORAGE_KEY: 'gist_share_ws_handoff',
  /**
   * Max age for stored WS credentials (matches 5 min token TTL).
   */
  MAX_AGE_MS: 5 * 60 * 1000
});

const ShareSlugValidation = Object.freeze({
  /**
   * Minimum accepted slug length for /try handoff.
   *
   * We intentionally allow a range instead of hard-coding the current
   * deterministic slug length so handoff remains forward-compatible if slug
   * generation changes.
   */
  MIN_LENGTH: 8,
  /**
   * Maximum accepted slug length for /try handoff.
   */
  MAX_LENGTH: 24,
  /**
   * URL-safe character set used by share slugs.
   */
  PATTERN: /^[A-Za-z0-9_-]+$/
});

export interface ShareNavigationOptions {
  response: IngestResponse;
  sharePromise: Promise<ShareResponse>;
  navigationMode?: ShareNavigationMode;
  onStatus?: (message: string) => void;
  connectWebSocket?: (options: TryWebSocketOptions) => { close(): void };
  location?: Location;
  sessionStorage?: Storage;
  now?: () => number;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}

export const ShareNavigationModes = Object.freeze({
  SAME_TAB: 'same_tab',
  NEW_TAB: 'new_tab'
} as const);

export type ShareNavigationMode =
  typeof ShareNavigationModes[keyof typeof ShareNavigationModes];

export interface ShareStreamingHandoffParams {
  share: ShareResponse | null;
  shareUrl: string;
  wsUrl: string;
  wsToken: string;
  location?: Location;
  sessionStorage?: Storage;
  now?: () => number;
}

/**
 * Resolves the share URL and optional WS handoff before navigation.
 *
 * @param options - Share navigation dependencies.
 * @returns Fully qualified share URL.
 */
export async function resolveShareNavigationTarget(options: ShareNavigationOptions): Promise<string> {
  const {
    response,
    sharePromise,
    navigationMode,
    onStatus,
    connectWebSocket,
    location,
    sessionStorage,
    now
  } = options;
  const resolvedNavigationMode = navigationMode ?? ShareNavigationModes.SAME_TAB;

  // New-tab submit flow should not wait for WS bootstrap frames.
  // Store same-origin handoff so the eventual ready-anchor click can open a
  // share page without refetching WS credentials.
  if (resolvedNavigationMode === ShareNavigationModes.NEW_TAB) {
    const share = await sharePromise;
    const shareUrl = resolveSharePublicUrl(share);
    if (response.wsUrl && response.wsToken) {
      storeShareStreamingHandoff({
        share,
        shareUrl,
        wsUrl: response.wsUrl,
        wsToken: response.wsToken,
        location,
        sessionStorage,
        now
      });
    }
    return shareUrl;
  }

  const canStream = Boolean(response.wsUrl && response.wsToken);
  const canRenderInline = Array.isArray(response.storyStream)
    && response.storyStream.length > 0;

  if (!canRenderInline && canStream) {
    const status = createGuardedStatus(onStatus);
    const bootstrapAbortController = new AbortController();
    status('Streaming...');
    const bootstrapPromise = waitForBootstrapFrame({
      contentId: response.contentId ?? '',
      wsUrl: response.wsUrl ?? '',
      wsToken: response.wsToken ?? '',
      onStatus: status,
      abortSignal: bootstrapAbortController.signal,
      connectWebSocket,
      setTimeoutFn: options.setTimeoutFn,
      clearTimeoutFn: options.clearTimeoutFn
    });
    try {
      const [, share] = await Promise.all([bootstrapPromise, sharePromise]);
      const shareUrl = resolveSharePublicUrl(share);
      storeShareStreamingHandoff({
        share,
        shareUrl,
        wsUrl: response.wsUrl ?? '',
        wsToken: response.wsToken ?? '',
        location,
        sessionStorage,
        now
      });
      return shareUrl;
    } finally {
      bootstrapAbortController.abort();
      status.dispose();
    }
  }

  const share = await sharePromise;
  const shareUrl = resolveSharePublicUrl(share);
  if (canStream) {
    storeShareStreamingHandoff({
      share,
      shareUrl,
      wsUrl: response.wsUrl ?? '',
      wsToken: response.wsToken ?? '',
      location,
      sessionStorage,
      now
    });
  }
  return shareUrl;
}

/**
 * Creates a status callback that can be disabled once flow settles.
 *
 * Why this exists:
 * - `waitForBootstrapFrame()` may still emit late status updates after the
 *   main submit flow has already failed/succeeded.
 * - Disabling updates prevents stale "Streaming..." text from overriding the
 *   final UI state.
 */
function createGuardedStatus(
  onStatus?: (message: string) => void
): ((message: string) => void) & { dispose(): void } {
  let active = true;

  const emit = ((message: string) => {
    if (!active) {
      return;
    }
    onStatus?.(message);
  }) as ((message: string) => void) & { dispose(): void };

  emit.dispose = () => {
    active = false;
  };

  return emit;
}

/**
 * Waits for the StoryStream bootstrap frame over WebSocket.
 *
 * @param params - WS + timeout options.
 */
async function waitForBootstrapFrame(params: {
  contentId: string;
  wsUrl: string;
  wsToken: string;
  onStatus?: (message: string) => void;
  abortSignal?: AbortSignal;
  connectWebSocket?: (options: TryWebSocketOptions) => { close(): void };
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}): Promise<void> {
  const {
    contentId,
    wsUrl,
    wsToken,
    onStatus,
    abortSignal,
    connectWebSocket,
    setTimeoutFn,
    clearTimeoutFn
  } = params;

  const connect = connectWebSocket ?? connectTryWebSocket;
  const scheduleTimeout = setTimeoutFn ?? setTimeout;
  const clearTimeoutLocal = clearTimeoutFn ?? clearTimeout;

  return await new Promise<void>((resolve) => {
    let resolved = false;
    let conn: { close(): void } | null = null;
    let timeoutId: ReturnType<typeof scheduleTimeout> | null = null;

    const onAbort = () => {
      try { conn?.close(); } catch { /* best-effort */ }
      done();
    };

    const clearTimeoutSafe = () => {
      if (timeoutId === null) {
        return;
      }
      clearTimeoutLocal(timeoutId);
      timeoutId = null;
    };

    const removeAbortListener = () => {
      try {
        abortSignal?.removeEventListener('abort', onAbort);
      } catch {
        // Best-effort.
      }
    };

    const done = () => {
      if (resolved) return;
      resolved = true;
      clearTimeoutSafe();
      removeAbortListener();
      resolve();
    };

    if (abortSignal?.aborted) {
      done();
      return;
    }
    abortSignal?.addEventListener('abort', onAbort, { once: true });

    timeoutId = scheduleTimeout(() => {
      onStatus?.('Streaming stalled. Opening share page...');
      try { conn?.close(); } catch { /* best-effort */ }
      done();
    }, StreamingTimeouts.BOOTSTRAP_TIMEOUT_MS);

    try {
      conn = connect({
        contentId,
        wsUrl,
        wsToken,
        onOpen: () => {
          if (!resolved) onStatus?.('Streaming...');
        },
        onMessage: (message) => {
          if (resolved) return;
          if (shouldNavigateOnBootstrap(message)) {
            try { conn?.close(); } catch { /* best-effort */ }
            done();
          }
        },
        onClose: () => {
          done();
        },
        onError: () => {
          // onClose will fire after this — let it handle cleanup.
        }
      });
    } catch {
      clearTimeoutLocal(timeoutId);
      done();
    }
  });
}

/**
 * Ensures the share response has a usable public URL.
 *
 * @param share - Share response from the backend.
 */
function resolveSharePublicUrl(share: ShareResponse | null): string {
  const publicUrl = typeof share?.publicUrl === 'string' ? share.publicUrl.trim() : '';
  if (!publicUrl) {
    throw new WebTryError('share_url_missing');
  }
  return publicUrl;
}

/**
 * Stashes WS credentials in sessionStorage for share-shell fast-path.
 *
 * @param params - Handoff payload + environment.
 */
export function storeShareStreamingHandoff(params: ShareStreamingHandoffParams): void {
  const session = params.sessionStorage ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
  const location = params.location ?? (typeof window !== 'undefined' ? window.location : null);
  if (!session || !location) {
    return;
  }
  if (!params.wsUrl || !params.wsToken) {
    return;
  }
  if (!isSameOriginShareUrl(params.shareUrl, location)) {
    return;
  }
  const slug = resolveShareSlug(params.share, params.shareUrl, location);
  if (!slug) {
    return;
  }
  try {
    const now = params.now ? params.now() : Date.now();
    const payload = {
      slug,
      wsUrl: params.wsUrl,
      wsToken: params.wsToken,
      storedAt: now,
      expiresAt: now + ShareStreamingHandoff.MAX_AGE_MS
    };
    session.setItem(ShareStreamingHandoff.STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Best-effort: share-shell will fall back to the ws-token endpoint.
  }
}

/**
 * Returns true when the share URL is same-origin with the /try page.
 */
function isSameOriginShareUrl(shareUrl: string, location: Location): boolean {
  try {
    const url = new URL(shareUrl, location.origin);
    return url.origin === location.origin;
  } catch {
    return false;
  }
}

/**
 * Resolves the share slug from a response or URL path.
 */
function resolveShareSlug(share: ShareResponse | null, shareUrl: string, location: Location): string | null {
  const directId = typeof share?.id === 'string' ? share.id.trim() : '';
  if (isPlausibleShareSlug(directId)) {
    return directId;
  }
  try {
    const url = new URL(shareUrl, location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    const candidate = parts[parts.length - 1] ?? '';
    if (isPlausibleShareSlug(candidate)) {
      return candidate;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Returns true when the value is a plausible share slug for handoff.
 */
function isPlausibleShareSlug(value: string): boolean {
  const slug = value.trim();
  if (!slug) {
    return false;
  }
  if (slug.length < ShareSlugValidation.MIN_LENGTH || slug.length > ShareSlugValidation.MAX_LENGTH) {
    return false;
  }
  return ShareSlugValidation.PATTERN.test(slug);
}
